// Inline editor for KnowledgeBase.subtypes — appears inside the KB editor
// expanded body. Lets master admins add / remove / tune the per-subtype
// score_floor + threshold_override that drive escalation decisions.
//
// Format of `value` (mirrors backend KnowledgeBase.subtypes):
//   [{ key, name, description, score_floor, threshold_override }]
//
// Empty list = legacy single-rubric grading.
import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, X, Sparkles, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_SUBTYPE = {
  key: '',
  name: '',
  description: '',
  score_floor: 40,
  threshold_override: null,
};

const slugify = (s) =>
  String(s || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 60);

export const KBSubtypesEditor = ({ value = [], onChange, kbId, disabled = false }) => {
  const [suggesting, setSuggesting] = useState(false);

  const set = (i, k, v) => {
    const next = [...value];
    next[i] = { ...next[i], [k]: v };
    onChange(next);
  };
  const add = () => onChange([...value, { ...EMPTY_SUBTYPE }]);
  const remove = (i) => onChange(value.filter((_, idx) => idx !== i));

  const suggestFromAI = async () => {
    if (!kbId) {
      toast.error('Save the knowledge base first, then let AI suggest subtypes.');
      return;
    }
    if (value.length > 0) {
      const ok = window.confirm(
        `Replace all ${value.length} existing subtypes with AI suggestions? (Cancel to keep them.)`,
      );
      if (!ok) return;
    }
    setSuggesting(true);
    try {
      const r = await axios.post(`${API}/knowledge-bases/${kbId}/suggest-subtypes`, {}, { timeout: 60000 });
      const subs = r.data?.subtypes || [];
      if (!subs.length) {
        toast.error('AI returned no subtypes — please add manually.');
        return;
      }
      onChange(subs);
      toast.success(`AI suggested ${subs.length} subtypes via ${r.data?.source || 'AI'} — review & save.`);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'AI suggestion failed';
      console.error('Subtype suggest failed', err?.response || err);
      toast.error(typeof detail === 'string' ? detail : 'AI suggestion failed');
    } finally {
      setSuggesting(false);
    }
  };

  return (
    <div data-testid="kb-subtypes-editor">
      <div className="flex items-center justify-between mb-2">
        <div>
          <p className="text-white/60 text-xs font-medium uppercase tracking-wide">
            Call Sub-Intents ({value.length})
          </p>
          <p className="text-white/30 text-[11px] mt-0.5">
            AI classifies each call into ONE subtype, then applies that subtype&apos;s
            floor + threshold. Empty = single rubric (legacy).
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={suggestFromAI}
            disabled={disabled || suggesting}
            className="flex items-center gap-1 text-purple-300 text-xs hover:text-purple-200 disabled:opacity-50"
            data-testid="kb-subtypes-ai-suggest"
            title="Let AI propose 3-4 sub-intents based on this KB's criteria & description"
          >
            {suggesting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
            {suggesting ? 'Thinking…' : 'AI Suggest'}
          </button>
          <button
            type="button"
            onClick={add}
            disabled={disabled}
            className="flex items-center gap-1 text-teal-400 text-xs hover:text-teal-300"
            data-testid="kb-subtypes-add"
          >
            <Plus className="w-3.5 h-3.5" />
            Add subtype
          </button>
        </div>
      </div>

      {value.length === 0 ? (
        <p className="text-white/20 text-xs text-center py-4">
          No subtypes yet — defaults are seeded on first save based on call_type.
        </p>
      ) : (
        <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
          {value.map((st, i) => (
            <div
              key={`kb-subtype-${i}`}
              className="bg-white/[0.03] border border-white/5 rounded-lg p-3 space-y-2"
              data-testid={`kb-subtype-row-${i}`}
            >
              <div className="flex items-start gap-2">
                <div className="flex-1 grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-[10px] text-white/30 uppercase">Name</label>
                    <input
                      value={st.name || ''}
                      onChange={(e) => {
                        const name = e.target.value;
                        set(i, 'name', name);
                        if (!st.key || st.key === slugify(st.name)) {
                          set(i, 'key', slugify(name));
                        }
                      }}
                      placeholder="Outbound Cold Telemarketing"
                      className="w-full bg-white/5 border border-white/10 rounded px-2 py-1 text-white text-xs focus:outline-none focus:border-teal-500/50"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] text-white/30 uppercase">Key (slug)</label>
                    <input
                      value={st.key || ''}
                      onChange={(e) => set(i, 'key', slugify(e.target.value))}
                      placeholder="outbound_cold_telemarketing"
                      className="w-full bg-white/5 border border-white/10 rounded px-2 py-1 text-white text-xs font-mono focus:outline-none focus:border-teal-500/50"
                    />
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => remove(i)}
                  className="text-white/20 hover:text-red-400 mt-4"
                  data-testid={`kb-subtype-remove-${i}`}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div>
                <label className="text-[10px] text-white/30 uppercase">
                  Description (used by AI to pick this subtype)
                </label>
                <textarea
                  value={st.description || ''}
                  onChange={(e) => set(i, 'description', e.target.value)}
                  rows={2}
                  placeholder="When the call is …"
                  className="w-full bg-white/5 border border-white/10 rounded px-2 py-1 text-white text-xs resize-none focus:outline-none focus:border-teal-500/50"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] text-white/30 uppercase">
                    Score Floor (min possible score)
                  </label>
                  <input
                    type="number"
                    min={0}
                    max={100}
                    value={st.score_floor ?? 0}
                    onChange={(e) => set(i, 'score_floor', parseInt(e.target.value || '0', 10))}
                    className="w-full bg-white/5 border border-white/10 rounded px-2 py-1 text-white text-xs focus:outline-none focus:border-teal-500/50"
                  />
                </div>
                <div>
                  <label className="text-[10px] text-white/30 uppercase">
                    Threshold Override (escalate below)
                  </label>
                  <input
                    type="number"
                    min={0}
                    max={100}
                    value={st.threshold_override ?? ''}
                    placeholder="(uses KB threshold)"
                    onChange={(e) => {
                      const v = e.target.value;
                      set(i, 'threshold_override', v === '' ? null : parseInt(v, 10));
                    }}
                    className="w-full bg-white/5 border border-white/10 rounded px-2 py-1 text-white text-xs focus:outline-none focus:border-teal-500/50"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
